/** packages/shared-components/src/components/KeyboardLayoutSelector/KeyboardLayoutSelector.tsx */

import React, { useMemo, useRef, useCallback } from "react";
import KeyboardLayoutSidebar from "./KeyboardLayoutSidebar";
import KeyboardLayoutContent from "./KeyboardLayoutContent";
import { KEY_LAYOUT_CATEGORIES } from "./keyboardLayoutData";
import "./KeyboardLayoutSelector.css";

const findCategoryIndex = (id: string) =>
  KEY_LAYOUT_CATEGORIES().findIndex((c) => c.id === id);

export interface KeyboardLayoutSelectorProps {
  selectedKeyCode?: number | null;
  onSelect?: (keyCode: number) => void;
  activeCategory: string;
  setActiveCategory: (categoryId: string) => void;
  className?: string;
  /** 禁用的 keyCode 列表（如未配置指令的宏），这些按键不可拖拽/点击 */
  disabledKeyCodes?: number[];
  /** 下方按键是否可拖拽到上方虚拟键盘改键 */
  draggable?: boolean;
  /** 用于KeyboardLayoutContent的键盘骨架屏兜底 */
  layoutLoading?: boolean;
}

export const KeyboardLayoutSelector: React.FC<KeyboardLayoutSelectorProps> = ({
  className,
  onSelect,
  activeCategory,
  setActiveCategory,
  disabledKeyCodes,
  draggable = true,
  layoutLoading,
}) => {
  /** 右侧可滚动内容区 */
  const contentScrollRef = useRef<HTMLDivElement>(null);
  /** 左侧点击时写入待滚动到的分组索引；滚动同步改类不会写入，用于区分是否执行 scrollIntoView */
  const pendingSidebarScrollToIndexRef = useRef<number | null>(null);

  const setActiveCategoryFromSidebar = useCallback(
    (categoryId: string) => {
      const idx = findCategoryIndex(categoryId);
      if (idx >= 0) pendingSidebarScrollToIndexRef.current = idx;
      setActiveCategory(categoryId);
    },
    [setActiveCategory]
  );

  // 当前类别索引
  const currentCategoryIndex = useMemo(() => {
    return KEY_LAYOUT_CATEGORIES().findIndex((c) => c.id === activeCategory);
  }, [activeCategory]);

  return (
    <div className={`keyboard-layout-selector ${className || ""}`}>
      <KeyboardLayoutSidebar
        activeCategory={activeCategory}
        setActiveCategory={setActiveCategoryFromSidebar}
      />
      <KeyboardLayoutContent
        scrollContainerRef={contentScrollRef}
        activeCategory={activeCategory}
        setActiveCategory={setActiveCategory}
        currentCategoryIndex={currentCategoryIndex}
        onSelect={onSelect}
        disabledKeyCodes={disabledKeyCodes}
        draggable={draggable}
        pendingSidebarScrollToIndexRef={pendingSidebarScrollToIndexRef}
        layoutLoading={layoutLoading}
      />
    </div>
  );
};

export default KeyboardLayoutSelector;
